/**
 * Micro-onboarding copy for Play Together — one idea per beat, WonderGuidance voice.
 * Use with formatMessageContent (**bold**) in the hub.
 */

import type { Language } from '@/lib/voiceManager';

type L = Language;

/**
 * Optional bump for release notes / analytics. The hub re-syncs welcome from `getWelcomeBlock`
 * whenever that string changes (see `expectedWelcomeContent` in PlayTogetherFloatingHub).
 */
export const PLAY_TOGETHER_MICRO_SCRIPT_REVISION = 28;

/**
 * True if the stored first-bubble text is an old script or differs from `getWelcomeBlock` (e.g. bold-only or extra lines).
 */
export function welcomeAssistantNeedsResync(stored: string, fresh: string): boolean {
  const n = stored.toLowerCase();
  if (n.includes('leave anytime') || n.includes('only an invitation')) return true;
  if (n.includes('i will guide you to explore our games')) return true;
  if (n.includes('solo una invitación') || n.includes('puedes irte cuando quieras')) return true;
  if (n.includes('te guiaré a explorar')) return true;
  /** Removed welcome paragraph; old bubbles still in memory / cache should refresh */
  if (n.includes('**next:**') || n.includes('next: a **short practice')) return true;
  if (n.includes('**siguiente:**')) return true;
  if (n.includes('wind in the branches') || n.includes('viento en las ramas')) return true;
  const norm = (s: string) =>
    s.replace(/\*\*/g, '').replace(/\*(?![*])/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
  return norm(stored) !== norm(fresh);
}

export function getWelcomeBlock(lang: L): string {
  return lang === 'en'
    ? 'Welcome. **WonderGuidance** is an **AI conversational interface** informed by Wonderpath’s approach to **sustainable, creative, expressive play**.'
    : 'Bienvenido/a. **WonderGuidance** es una **interfaz conversacional con IA** basada en el enfoque Wonderpath al juego **sostenible, creativo y expresivo**.';
}

/** Fixed assistant line after "I'm ready — let's continue" (micro_pull). */
export function getPullBlock(lang: L): string {
  return lang === 'en'
    ? 'We will play a brief play simulation using the Wonderpath approach for sustainable, creative, expressive play. Choose one focus below.'
    : 'Jugaremos una breve simulación de juego con el enfoque Wonderpath: juego sostenible, creativo y expresivo. Elige un enfoque abajo.';
}

/** Normalize for comparing model output to our fixed copy (bold/spacing). */
function normPlain(s: string): string {
  return s.replace(/\*\*/g, '').replace(/\*(?![*])/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * Models often echo the fixed pull line in the “supplement” even when instructed not to.
 * Strips leading paragraph(s) that duplicate our fixed line so we don’t show it twice.
 */
export function dedupePullSupplementAgainstFixedLine(supplement: string, fixedLine: string): string {
  const baseNorm = normPlain(fixedLine);
  if (!baseNorm) return supplement.trim();
  let paras = supplement.trim().split(/\n\n+/);
  while (paras.length > 0) {
    const first = paras[0]?.trim() ?? '';
    if (!first) {
      paras = paras.slice(1);
      continue;
    }
    const firstNorm = normPlain(first);
    const n = Math.min(52, baseNorm.length, firstNorm.length);
    const sameStart = n >= 16 && firstNorm.slice(0, n) === baseNorm.slice(0, n);
    const sameOrContained =
      firstNorm === baseNorm ||
      sameStart ||
      (firstNorm.length >= 20 && baseNorm.includes(firstNorm.slice(0, Math.min(36, firstNorm.length)))) ||
      (firstNorm.length >= 20 && firstNorm.includes(baseNorm.slice(0, Math.min(36, baseNorm.length))));
    if (sameOrContained) {
      paras = paras.slice(1);
      continue;
    }
    break;
  }
  return paras.join('\n\n').trim();
}

/**
 * Beat — games grid (after playthrough / simulation): one line in the script, cards carry names.
 */
export function getGamesIntroBlock(lang: L): string {
  return lang === 'en'
    ? '**Choose a game** below when you’re ready. Each one supports **sustainable, creative, expressive** play in a different way.'
    : '**Elige un juego** abajo cuando quieras. Cada uno apoya el juego **sostenible, creativo y expresivo** de un modo distinto.';
}

export function getScaffoldHint(lang: L): string {
  return lang === 'en'
    ? 'If you’re not sure, start with what feels easiest—**there is no wrong door**.'
    : 'Si no estás seguro/a, empieza con lo que se sienta más fácil—**no hay puerta incorrecta**.';
}

/** After a practice round: hand off to the games step (no per-title CTAs—the cards do that). */
export function getPlayTogetherPostSimHandoff(lang: L): string {
  return lang === 'en'
    ? '**Next step:** When you’re ready, **pick a game** from the cards below—you can come back for another practice anytime.'
    : '**Siguiente paso:** Cuando quieras, **elige un juego** en las tarjetas de abajo—puedes volver para otra práctica cuando quieras.';
}

export const microLabels = {
  /** First step: under welcome message */
  continueCta: { en: 'Start Guidance', es: 'Comenzar la guía' },
  /** Shown as the user’s line after tapping Continue, before the “pull” question */
  userReady: { en: "I'm ready — let's continue", es: 'Listo/a — sigamos' },
  /** Label above the four focus chips */
  pullChipLead: {
    en: "Select what you're most curious to try",
    es: 'Selecciona lo que más te gustaría probar',
  },
  quickReplies: [
    {
      userLine: { en: 'Develop Early Literacy', es: 'Desarrollar alfabetización temprana' },
    },
    {
      userLine: { en: 'Practice Breathing Mindfulness', es: 'Practicar mindfulness y la respiración' },
    },
    {
      userLine: { en: 'Draw Imagination', es: 'Dibujar e imaginar' },
    },
    {
      userLine: { en: 'Express Ideas and Feelings', es: 'Expresar ideas y sentimientos' },
    },
  ] as const,
  trySimulation: {
    en: 'Practice longer play simulations',
    es: 'Practica simulaciones de juego más largas',
  },
  openGuide: { en: 'Open the full Play Together guide', es: 'Abrir la guía completa de Juguemos Juntos' },
  simHint: {
    en: 'WonderGuidance can guide longer simulations when you want more depth before you pick a game.',
    es: 'WonderGuidance puede guiar simulaciones más largas cuando quieras más profundidad antes de elegir un juego.',
  },
  /** After literacy micro-sim: primary jump into Animal Alphabet */
  openAnimalAbcsCta: {
    en: 'Open Animal ABCs to practice literacy',
    es: 'Abrir ABCs de animales para practicar lectura',
  },
  /** After breathing micro-sim */
  openPepeKnowsCta: {
    en: 'Open Pepe Knows for a short round',
    es: 'Abrir Pepe Sabe para una ronda corta',
  },
  /** After draw micro-sim */
  openSuperpowersCta: {
    en: 'Open Superpowers for a short round',
    es: 'Abrir Superpoderes para una ronda corta',
  },
  /** After express micro-sim */
  openPekeAdventuresCta: {
    en: "Open Peke's Adventures for a short round",
    es: 'Abrir las Aventuras de Peke para una ronda corta',
  },
  /** Label above the four focus chips when re-offering other simulations */
  postLiteracyOtherPracticesLead: {
    en: 'Or try a short practice for another focus.',
    es: 'O prueba una práctica breve para otro enfoque.',
  },
  /** Pill on the game tile that matches the completed micro-sim */
  suggestedGameTileBadge: {
    en: 'Suggested',
    es: 'Sugerido',
  },
};

/** Hub follow-up chips (games step + empty chat) — keep in sync with product script. */
export const playTogetherHubFollowUpChips: readonly { en: string; es: string }[] = [
  {
    en: 'Explore Why ecology and sustainable play matter',
    es: 'Explora por qué importan la ecología y el juego sostenible',
  },
  {
    en: 'Ideas for hands-on play with scraps',
    es: 'Ideas para juego práctico con retazos',
  },
  {
    en: 'Ideas for sustainable, creative, and expressive play',
    es: 'Ideas para juego sostenible, creativo y expresivo',
  },
];
